<template>
  <nav class="flex-1 space-y-3 px-5 py-6" style="--nav-theme-color: #112036;">
    <!-- Dashboard -->
    <Link
      :href="$routes.admin_index()"
      :class="[
        'group nav-link text-xs font-bold uppercase tracking-wider',
        isExactUrl('admin') ? 'nav-link-active' : 'nav-link-inactive',
      ]"
    >
      <span>Dashboard</span>
    </Link>

    <!-- Global resources (no site context) -->
    <div class="space-y-1">
      <div v-for="resource in resourceNavItems" :key="resource.key" class="space-y-0.5">
        <Link
          :href="resource.listHref"
          :class="[
            'group nav-link text-xs font-bold uppercase tracking-wider',
            isUrl(resource.listUrlPrefix) ? 'nav-link-active' : 'nav-link-inactive',
          ]"
        >
          <span>{{ resource.label }}</span>
        </Link>
        <div class="ml-3 space-y-0.5">
          <Link
            v-if="resourcePage(resource.kind, 'resource_index')"
            :href="$routes.admin_resource_page_configuration({
              page: resourcePage(resource.kind, 'resource_index').slug,
            })"
            :class="[
              'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-[11px] font-medium',
              isResourcePageActive(resource.kind, 'resource_index')
                ? 'text-white'
                : 'text-white/75 hover:text-white',
            ]"
          >
            <span>Konfiguracja list</span>
            <icon name="cog" class="h-3.5 w-3.5 shrink-0 stroke-current opacity-40" />
          </Link>
          <Link
            v-if="resourcePage(resource.kind, 'resource_show')"
            :href="$routes.admin_resource_page_configuration({
              page: resourcePage(resource.kind, 'resource_show').slug,
            })"
            :class="[
              'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-[11px] font-medium',
              isResourcePageActive(resource.kind, 'resource_show')
                ? 'text-white'
                : 'text-white/75 hover:text-white',
            ]"
          >
            <span>Konfiguracja stron</span>
            <icon name="cog" class="h-3.5 w-3.5 shrink-0 stroke-current opacity-40" />
          </Link>
        </div>
      </div>
      <Link
        :href="$routes.admin_carousel_posts()"
        :class="[
          'group nav-link mb-8 text-xs font-bold uppercase tracking-wider',
          isUrl('admin/carousel-posts') ? 'nav-link-active' : 'nav-link-inactive',
        ]"
      >
        <span>Posty karuzeli</span>
      </Link>
      <Link
        :href="$routes.admin_languages()"
        :class="[
          'group nav-link',
          isUrl('admin/jezyki') ? 'nav-link-active' : 'nav-link-inactive',
        ]"
      >
        <span>Języki</span>
      </Link>
      <Link
        :href="$routes.admin_booking_interests()"
        :class="[
          'group nav-link',
          isUrl('admin/prosby-o-terminy') ? 'nav-link-active' : 'nav-link-inactive',
        ]"
      >
        <span>Prośby o terminy</span>
      </Link>
      <Link
        :href="$routes.admin_navigation_pages()"
        :class="[
          'group nav-link',
          isUrl('admin/navigation-pages') ? 'nav-link-active' : 'nav-link-inactive',
        ]"
      >
        <span>Strony nawigacji</span>
      </Link>
      <Link
        v-if="isAdmin"
        :href="$routes.users()"
        :class="[
          'group nav-link',
          isUrl('users') ? 'nav-link-active' : 'nav-link-inactive',
        ]"
      >
        <span>Użytkownicy</span>
      </Link>
      <Link
        :href="$routes.admin_visits()"
        :class="[
          'group nav-link',
          isUrl('admin/wizyty') ? 'nav-link-active' : 'nav-link-inactive',
        ]"
      >
        <span>Wizyty i płatności</span>
      </Link>
    </div>

    <!-- WITRYNY (per-site admins; code_medical_center is a normal site now) -->
    <div class="space-y-1">
      <div
        class="flex items-center px-3 py-2 text-xs font-bold uppercase tracking-wider text-white/90 select-none"
      >
        <span>Witryny</span>
      </div>

      <div class="ml-4 space-y-1 mt-2">
        <Link
          v-for="site in sites"
          :key="site.key"
          :href="site.href"
          class="group nav-link text-white shadow-sm hover:opacity-90 hover:shadow-md"
          :style="{ backgroundColor: site.color }"
        >
          <span>{{ site.label }}</span>
        </Link>
      </div>
    </div>

    <!-- GLOBAL BLOCKS (system-wide chrome: Main Header + Main Footer; global admin only) -->
    <div class="p-2 bg-white/10 rounded-md">
      <h1 class="text-lg font-bold text-white/90 mb-2 px-3">Bloki Globalne</h1>

      <div class="space-y-1">
        <Link
          :href="$routes.layout_admin_blocks()"
          :class="[
            'group nav-link',
            isUrl('admin/blocks/layout')
              ? 'nav-link-active'
              : 'nav-link-inactive',
          ]"
        >
          <span>Nagłówek i stopka</span>
        </Link>
      </div>
    </div>
  </nav>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';

const RESOURCE_PAGES_SITE_CONTEXT = 'code_medical_center';

export default {
  components: {
    Link,
    Icon,
  },
  computed: {
    isAdmin() {
      return this.$page.props.auth?.user?.role === 'admin';
    },
    resourceNavItems() {
      return [
        {
          key: 'specialists',
          kind: 'specialist',
          label: 'Specjaliści',
          listHref: this.$routes.admin_specialists(),
          listUrlPrefix: 'admin/specjalisci',
        },
        {
          key: 'services',
          kind: 'service',
          label: 'Usługi',
          listHref: this.$routes.admin_services(),
          listUrlPrefix: 'admin/uslugi',
        },
        {
          key: 'specialisations',
          kind: 'specialisation',
          label: 'Specjalizacje',
          listHref: this.$routes.admin_specialisations(),
          listUrlPrefix: 'admin/specjalizacje',
        },
        {
          key: 'articles',
          kind: 'article',
          label: 'Aktualności',
          listHref: this.$routes.admin_articles(),
          listUrlPrefix: 'admin/aktualnosci',
        },
      ];
    },
    resourcePagesByKind() {
      const all = this.$page.props.resource_pages_by_site_context || {};
      const pages = all[RESOURCE_PAGES_SITE_CONTEXT] || [];
      const byKind = {};

      pages.forEach((page) => {
        if (!byKind[page.resource_kind]) byKind[page.resource_kind] = {};
        byKind[page.resource_kind][page.page_type] = page;
      });

      return byKind;
    },
    // Per-site admin links, each rendered in that site's brand color
    // (see --color-* tokens in styles/application.css).
    sites() {
      return [
        {
          key: 'code_medical_center',
          label: 'Centrum Medyczne CODE',
          color: 'var(--color-code-medical-center)',
          href: this.$routes.admin_code_medical_center(),
        },
        {
          key: 'infertility_treatment_clinic',
          label: 'Klinika Przyczynowego Leczenia Niepłodności',
          color: '#831843',
          href: this.$routes.admin_infertility_treatment_clinic(),
        },
        {
          key: 'family_clinic',
          label: 'Klinika Zdrowia Rodziny',
          color: '#134e4a',
          href: this.$routes.admin_family_clinic(),
        },
        {
          key: 'institute',
          label: 'Instytut',
          color: '#4c1d95',
          href: this.$routes.admin_institute(),
        },
      ];
    },
  },
  methods: {
    resourcePage(kind, pageType) {
      return this.resourcePagesByKind[kind]?.[pageType] || null;
    },
    isResourcePageActive(kind, pageType) {
      const page = this.resourcePage(kind, pageType);
      if (!page) return false;
      const currentUrl = this.$page.url.slice(1).replace(/\/$/, '');
      return currentUrl === `admin/strony/${page.slug}/configuration`;
    },
    isUrl(...urls) {
      const currentUrl = this.$page.url.slice(1);
      return urls.filter((url) => currentUrl.startsWith(url)).length > 0;
    },
    isExactUrl(url) {
      let currentUrl = this.$page.url.slice(1);
      currentUrl = currentUrl.replace(/\/$/, '');
      url = url.replace(/\/$/, '');
      return currentUrl === url;
    },
  },
};
</script>
